import { useCallback, useEffect, useRef, useState } from "react";

import "animate.css";
import clsx from "clsx";
import { motion, useAnimation } from "framer-motion";
import "react-modern-drawer/dist/index.css";
import { Link } from "react-router-dom";
import { useToasts } from "react-toast-notifications";
import { twMerge } from "tailwind-merge";
import { useDebounce, useOnClickOutside } from "usehooks-ts";

import { useNavbarExpanded } from "@/atoms/navbar-atom";
import { usePrettyLogEventPage } from "@components/v2/analytics";
import { useUniversalBenchmarksModalContext } from "@features/universal-benchmarks";
import { useAppcuesBannerHeight } from "@hooks/appcues-banner-height";
import { useFeatureFlags } from "@hooks/http";
import { useForceOnboarding, useInOnboarding } from "@hooks/use-in-onboarding";
import { useToggleNBV2 } from "@hooks/use-toggle-nb-v2";
import { prettyLogEvent } from "@internal/analytics";
import { Pages } from "@north-beam/nb-client-side/constants";
import { Checkbox } from "@shared/checkbox";
import { ContextMenu } from "@shared/context-menu";
import { Select } from "@shared/selects";
import { usePrefs } from "@utils/client-side-preferences";

import { GenericExternalLink } from "../generic-external-link";
import { useUser } from "../user-context";

import { sidebarItemClasses } from "./nav-link";
import { SidebarItem, sidebarIconClasses } from "./sidebar-item";
import { SidebarItemWithAccordion } from "./sidebar-item-with-accordion";
import {
  SidebarItemWithSubitems,
  SidebarSubitem,
} from "./sidebar-item-with-subitems";

interface Props {
  onShowAdminModal: () => void;
}

export function CollapsibleSidebar({ onShowAdminModal }: Props) {
  const { user } = useUser();
  const toggleNBV2 = useToggleNBV2();

  const { hasFeatureFlag } = useFeatureFlags();
  const controls = useAnimation();
  const navbarRef = useRef<HTMLDivElement>(null);

  const [expanded, setExpanded] = useNavbarExpanded();
  const debouncedExpanded = useDebounce<boolean>(expanded, 100);
  const appcuesBannerHeight = useAppcuesBannerHeight();
  const { openUniversalBenchmarksModal } = useUniversalBenchmarksModalContext();

  const { useDevelopmentAnalyticsData } = usePrefs();

  useOnClickOutside(navbarRef, () => {
    setExpanded(false);
  });

  useEffect(() => {
    if (debouncedExpanded) {
      controls.start("expanded");
    } else {
      controls.start("collapsed");
    }
  }, [controls, debouncedExpanded]);

  const prettyLogEventPage = usePrettyLogEventPage();

  return (
    <motion.div
      ref={navbarRef}
      animate={controls}
      initial="collapsed"
      className={clsx(
        "flex flex-col justify-between mt-14 fixed z-[31] text-white overflow-x-hidden",
        {
          "bg-interstellar": !useDevelopmentAnalyticsData,
          "bg-venus border-r": useDevelopmentAnalyticsData,
        },
      )}
      transition={{ duration: 0.1 }}
      variants={{
        expanded: { maxWidth: "236px", minWidth: "236px" },
        collapsed: { maxWidth: "56px", minWidth: "56px" },
      }}
      style={{ height: `calc(100% - 56px - ${appcuesBannerHeight}px)` }}
      onMouseEnter={() => setExpanded(true)}
      onMouseLeave={() => setExpanded(false)}
    >
      <div className="flex flex-col space-y-4 my-4">
        <SidebarItem
          to="overview"
          icon={<i className="fa-regular fa-house" />}
          expanded={debouncedExpanded}
          useDevelopmentAnalyticsData={useDevelopmentAnalyticsData}
        >
          Overview
        </SidebarItem>
        {hasFeatureFlag("productAnalytics") ? (
          <SidebarItemWithAccordion
            icon={<i className="fa-regular fa-chart-line" />}
            expanded={debouncedExpanded}
            useDevelopmentAnalyticsData={useDevelopmentAnalyticsData}
            title="Attribution"
            isNew={hasFeatureFlag("enableUniversalBenchmarks") ? false : true}
          >
            <SidebarSubitem
              to="sales"
              expanded={debouncedExpanded}
              useDevelopmentAnalyticsData={useDevelopmentAnalyticsData}
            >
              Sales Attribution
            </SidebarSubitem>
            <SidebarSubitem
              to="product-analytics"
              expanded={debouncedExpanded}
              useDevelopmentAnalyticsData={useDevelopmentAnalyticsData}
            >
              Product Attribution
            </SidebarSubitem>
          </SidebarItemWithAccordion>
        ) : (
          <SidebarItem
            to="sales"
            icon={<i className="fa-regular fa-chart-line" />}
            expanded={debouncedExpanded}
            useDevelopmentAnalyticsData={useDevelopmentAnalyticsData}
          >
            Sales
          </SidebarItem>
        )}
        <SidebarItem
          to="customers"
          end
          icon={<i className="fa-regular fa-chart-bar" />}
          expanded={debouncedExpanded}
          useDevelopmentAnalyticsData={useDevelopmentAnalyticsData}
        >
          Customer LTV
        </SidebarItem>
        {/* Show the orders tab if the featureFlag has not been set or the user has set the
            feature flag to true*/}
        <SidebarItem
          to="orders"
          onClick={() => {
            prettyLogEvent.navigateToPage({
              page: Pages.ORDERS,
              user,
            });
          }}
          icon={<i className="fa-regular fa-cart-shopping" />}
          expanded={debouncedExpanded}
          useDevelopmentAnalyticsData={useDevelopmentAnalyticsData}
        >
          Orders
        </SidebarItem>
        <SidebarItem
          isHidden={!user.featureFlags.enableCreativeAnalytics}
          onClick={() => {
            prettyLogEvent.navigateToPage({
              page: Pages.CREATIVE_ANALYTICS,
              user,
            });
          }}
          to="creative-analytics"
          icon={<i className="fa-regular fa-image" />}
          expanded={debouncedExpanded}
          useDevelopmentAnalyticsData={useDevelopmentAnalyticsData}
        >
          Creative Analytics
        </SidebarItem>
        <SidebarItem
          isHidden={!user.featureFlags.enableMetricsExplorer}
          onClick={() => {
            prettyLogEvent.navigateToPage({
              page: Pages.METRICS_EXPLORER,
              user,
            });
          }}
          to="metrics-explorer"
          icon={<i className="fa-regular fa-telescope" />}
          expanded={debouncedExpanded}
          useDevelopmentAnalyticsData={useDevelopmentAnalyticsData}
        >
          Metrics Explorer
        </SidebarItem>
        <SidebarItem
          isHidden={!user.featureFlags.enableMMM}
          onClick={() => {
            prettyLogEvent.navigateToPage({
              user,
              page: Pages.MMM,
            });
          }}
          to="mmm"
          icon={<i className="fa-regular fa-compass" />}
          expanded={debouncedExpanded}
          useDevelopmentAnalyticsData={useDevelopmentAnalyticsData}
        >
          MMM+
        </SidebarItem>
        {hasFeatureFlag("enableUniversalBenchmarks") && (
          <button onClick={() => openUniversalBenchmarksModal()}>
            <div
              className={twMerge(
                sidebarItemClasses,
                "flex flex-row items-center",
              )}
            >
              <div className="relative">
                <span
                  className={sidebarIconClasses(useDevelopmentAnalyticsData)}
                >
                  <i className="fa-regular fa-dumbbell" />
                </span>
              </div>
              <motion.span
                className={clsx("ml-2", {
                  "pointer-events-none hidden": !debouncedExpanded,
                })}
                animate={{ opacity: debouncedExpanded ? 1 : 0 }}
                transition={{ duration: 0.1 }}
              >
                Profitability Benchmarks
              </motion.span>
            </div>
          </button>
        )}
        <SidebarItem
          isHidden={
            !(
              user.displayUserId === "dd62a9c5-ca2d-4231-a4bf-0cb5acc56c49" &&
              user.featureFlags.showFarmstandPurchases
            )
          }
          to="farmstand-purchases"
          icon={<i className="fa-regular fa-store" />}
          expanded={debouncedExpanded}
          useDevelopmentAnalyticsData={useDevelopmentAnalyticsData}
        >
          Farmstand Purchases
        </SidebarItem>
        <SidebarItemWithSubitems
          isHidden={
            !(
              user.featureFlags.enableLandingPageAnalyticsDashboard ||
              user.featureFlags.notificationsLab
            )
          }
          to="labs"
          icon={<i className="fa-regular fa-flask" />}
          expanded={debouncedExpanded}
          useDevelopmentAnalyticsData={useDevelopmentAnalyticsData}
          title="Northbeam Labs"
        >
          <SidebarSubitem
            isHidden={!user.featureFlags.enableLandingPageAnalyticsDashboard}
            to="labs/landing-page-analytics"
            expanded={debouncedExpanded}
            useDevelopmentAnalyticsData={useDevelopmentAnalyticsData}
          >
            Landing Page Analytics
          </SidebarSubitem>
          <SidebarSubitem
            isHidden={!user.featureFlags.enableNotificationsLab}
            to="labs/notifications"
            expanded={debouncedExpanded}
            useDevelopmentAnalyticsData={useDevelopmentAnalyticsData}
          >
            Notifications
          </SidebarSubitem>
        </SidebarItemWithSubitems>
      </div>
      <div className="flex flex-col space-y-4 my-4">
        {/* note: the AdminMenu will return null if we shouldn't be rendering admin stuff */}
        <AdminMenu
          expanded={debouncedExpanded}
          useDevelopmentAnalyticsData={useDevelopmentAnalyticsData}
          onShowAdminModal={onShowAdminModal}
        />
        <button
          onClick={async () => {
            prettyLogEventPage({
              domain: "Dashboard V2",
              action: "onClick",
              additionalActionInfo: "Try Northbeam 2.0",
            });
            await toggleNBV2();
          }}
          className={twMerge(sidebarItemClasses, "text-left")}
        >
          <div className="relative">
            <span
              className={twMerge(
                sidebarIconClasses(useDevelopmentAnalyticsData),
                "text-xxs border-2 border-v2-action bg-[#9d050b4d] shadow-[0_0_10px] shadow-[#9d050b] leading-[9px] font-bold",
              )}
            >
              NB
              <br />
              2.0
            </span>
            <div
              className={
                "absolute -bottom-2 left-1 transform " +
                "text-xxs border-interstellar border-2 bg-v2-priority-3 text-white rounded-lg px-1"
              }
            >
              BETA
            </div>
          </div>
          <motion.span
            animate={{ opacity: debouncedExpanded ? 1 : 0 }}
            transition={{ duration: 0.1 }}
            className="text-left"
          >
            Try Northbeam 2.0
          </motion.span>
        </button>
        <a
          href="https://docs.northbeam.io/docs/what-is-northbeam"
          target="_blank"
          rel="noopener noreferrer"
          style={{ all: "unset" }}
          className={twMerge(sidebarItemClasses, "text-left")}
        >
          <span className={sidebarIconClasses(useDevelopmentAnalyticsData)}>
            <i className="fa-regular fa-circle-question" />
          </span>
          <motion.span
            animate={{ opacity: debouncedExpanded ? 1 : 0 }}
            transition={{ duration: 0.1 }}
            className="text-left"
          >
            Help
          </motion.span>
        </a>
        <SidebarItem
          to="settings"
          icon={<i className="fa-regular fa-cog" />}
          expanded={debouncedExpanded}
          useDevelopmentAnalyticsData={useDevelopmentAnalyticsData}
        >
          Settings
        </SidebarItem>
      </div>
    </motion.div>
  );
}

export const AdminMenu = ({
  expanded = true,
  useDevelopmentAnalyticsData,
  compactDesign,
  onShowAdminModal,
}: {
  expanded?: boolean;
  useDevelopmentAnalyticsData: boolean;
  compactDesign?: boolean;
  onShowAdminModal: () => void;
}) => {
  const { hasFeatureFlag } = useFeatureFlags();
  const { user, actingUser } = useUser();
  const { setIsForceOnboarding } = useForceOnboarding();

  const { inOnboarding } = useInOnboarding();
  const { addToast } = useToasts();

  const prefs = usePrefs();
  const toggleNBV2 = useToggleNBV2();

  const setUseDevelopmentAnalyticsDataLocal = useCallback(async () => {
    await prefs.setUseDevelopmentAnalyticsData(
      !prefs.useDevelopmentAnalyticsData,
    );

    window.location.reload();
  }, [prefs]);

  const setDemoMode = useCallback(async () => {
    await prefs.setDemoMode(!prefs.isDemoMode);

    window.location.reload();
  }, [prefs]);

  const setClearActing = useCallback(async () => {
    actingUser.setActingUserId(null);

    window.location.reload();
  }, [actingUser]);

  const shouldShowAnonymizationSwitch =
    user.isAdmin || user.featureFlags.showAnonymizationSwitch;

  const isActing = Boolean(actingUser.actingUserId);
  const shouldShowDevelopmentAnalyticsTableSwitch =
    user.isAdmin || user.featureFlags.showDevelopmentAnalyticsTableSwitch;
  const shouldShowRetoolLink = user.isAdmin;
  const shouldShowForceOnboarding = user.isAdmin;
  const shouldShowAdminModal = user.isAdmin;

  const shouldShowAdminMenu =
    shouldShowAnonymizationSwitch ||
    shouldShowDevelopmentAnalyticsTableSwitch ||
    shouldShowRetoolLink ||
    shouldShowForceOnboarding ||
    shouldShowAdminModal;

  const noDevelopmentTables = !prefs.availableDevelopmentTables.length;

  if (!shouldShowAdminMenu && !isActing) {
    return null;
  }

  return (
    <ContextMenu
      className="z-20"
      menuClassName="w-64"
      menuPosition="top-right"
      menuButton={
        <ContextMenu.Button
          className={twMerge(
            sidebarItemClasses,
            clsx({
              "flex  items-center h-[38px] px-2 rounded-lg mx-5  cursor-pointer hover:bg-gray-1":
                compactDesign,
            }),
          )}
        >
          {!compactDesign && (
            <span className={sidebarIconClasses(useDevelopmentAnalyticsData)}>
              <i className="fa-regular fa-user " />
            </span>
          )}
          {compactDesign && (
            <span className="mr-3 text-primary">
              <i className="fa-regular fa-user" />
            </span>
          )}
          <motion.span
            animate={{ opacity: expanded ? 1 : 0 }}
            transition={{ duration: 0.1 }}
          >
            Admin tools
          </motion.span>
        </ContextMenu.Button>
      }
    >
      {expanded && (
        <>
          {shouldShowAnonymizationSwitch && (
            <ContextMenu.Item
              as="div"
              className="cursor-pointer flex items-center space-x-1 py-3 px-4 text-interstellar"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onClick={setDemoMode}
            >
              <Checkbox value={prefs.isDemoMode} />
              <span>Anonymize data</span>
            </ContextMenu.Item>
          )}
          {isActing && (
            <ContextMenu.Item
              as="div"
              className="cursor-pointer flex items-center space-x-1 py-3 px-4 text-interstellar"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onClick={setClearActing}
            >
              <Checkbox value={isActing} />
              <span>Impersonating</span>
            </ContextMenu.Item>
          )}
          {shouldShowDevelopmentAnalyticsTableSwitch && (
            <ContextMenu.Item
              as="div"
              className={clsx("flex flex-col  py-2 px-4 text-interstellar", {
                " cursor-not-allowed": noDevelopmentTables,
                "cursor-pointer": !noDevelopmentTables,
              })}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onClick={() => {
                if (noDevelopmentTables) {
                  addToast("No development tables available", {
                    appearance: "error",
                  });
                  return;
                }

                if (
                  !prefs.availableDevelopmentTables.includes(
                    prefs.developmentTable ?? "",
                  )
                ) {
                  addToast("Please select a development table to use", {
                    appearance: "error",
                  });
                  return;
                }
                setUseDevelopmentAnalyticsDataLocal();
              }}
            >
              <div className="flex items-center space-x-1">
                <Checkbox
                  isDisabled={noDevelopmentTables}
                  value={prefs.useDevelopmentAnalyticsData}
                />
                <span>Use development data</span>
              </div>
              <div
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onChange={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <Select
                  width="13rem"
                  label="Select the development table to use"
                  isLoading={prefs.availableDevelopmentTablesLoading}
                  value={prefs.developmentTable}
                  options={prefs.availableDevelopmentTables.map((table) => {
                    return {
                      label: table,
                      value: table,
                    };
                  })}
                  onChange={async (value) => {
                    prefs.setDevelopmentTable(value);
                    await prefs.setUseDevelopmentAnalyticsData(true);

                    window.location.reload();
                  }}
                  name="development-tables"
                />
              </div>
            </ContextMenu.Item>
          )}
          {inOnboarding && shouldShowForceOnboarding && (
            <ContextMenu.Item
              as="div"
              onMouseDown={async (e) => {
                e.preventDefault();
                e.stopPropagation();
                await setIsForceOnboarding(false);
                window.location.reload();
              }}
            >
              <Link
                to="/"
                className="no-underline block h-full text-cosmic py-2 px-4"
              >
                Exit User Onboarding
              </Link>
              <span></span>
            </ContextMenu.Item>
          )}
          {!inOnboarding && shouldShowForceOnboarding && (
            <ContextMenu.Item
              as="div"
              onMouseDown={async (e) => {
                e.preventDefault();
                e.stopPropagation();
                await setIsForceOnboarding(true);
                window.location.reload();
              }}
            >
              <Link
                to="onboarding"
                className="no-underline block h-full text-cosmic py-2 px-4"
              >
                Go to User Onboarding
              </Link>
              <span></span>
            </ContextMenu.Item>
          )}
          {shouldShowRetoolLink && (
            <ContextMenu.Item
              as="div"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <Link
                to="settings/northbeam-labs"
                className="no-underline block h-full text-cosmic py-2 px-4"
              >
                Northbeam Labs
              </Link>
            </ContextMenu.Item>
          )}
          {shouldShowRetoolLink && (
            <ContextMenu.Item
              as="div"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <GenericExternalLink
                className="no-underline block text-cosmic py-2 px-4"
                href={`https://northbeam.retool.com/apps/fbf147e0-c253-11eb-8912-ef20e112236b/Northbeam#userId=${user.displayUserId}`}
              >
                Open Retool
              </GenericExternalLink>
            </ContextMenu.Item>
          )}
          {shouldShowAdminModal && (
            <ContextMenu.Item as="div" onMouseDown={(e) => {}}>
              <a
                className="no-underline block h-full text-cosmic py-2 px-4"
                onClick={() => {
                  onShowAdminModal();
                }}
              >
                BQ SQL Logs
              </a>
            </ContextMenu.Item>
          )}
          <ContextMenu.Item
            as="div"
            className="text-cosmic py-2 px-4 cursor-pointer"
            onMouseDown={async () => {
              await toggleNBV2();
            }}
          >
            To {hasFeatureFlag("enableDashboardV2") ? "Old" : "New"} UI{" "}
            <i className="fa-solid fa-right"></i>
          </ContextMenu.Item>
        </>
      )}
    </ContextMenu>
  );
};
